import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import cx from "classnames";

import { LayoutUtilProps } from "../../types";
import { useMergeRefs } from "../../hooks";
import { useLayoutPropsUtil } from "../../internal/hooks";
import styles from "./SideNav.module.scss";
import { SideNavLink } from "./SideNavLink";
import { SideNavCollapsible } from "./SideNavCollapsible";
import { SideNavGroup } from "./SideNavGroup";
import { SideNavProvider } from "./internal/SideNavProvider";
import { DataTrackingId } from "../../types";
import { SideNavContext, SideNavContextProps } from "./SideNavContext";

/**
 * Props for the SideNav component
 * @extends ComponentPropsWithoutRef<"nav">
 * @extends LayoutUtilProps
 */
export type SideNavProps = ComponentPropsWithoutRef<"nav"> &
  LayoutUtilProps &
  DataTrackingId;

/**
 * SideNav component for creating hierarchical navigation menus.
 *
 * Features:
 * - Hierarchical navigation structure with links, groups, and collapsible sections
 * - Keyboard navigation support with arrow keys and focus management
 * - Active state tracking for current page/section
 * - Accessible with proper ARIA roles and labels
 * - Automatic link recalculation for dynamic content
 * - Context provider for child components
 * - Layout utility props for positioning and spacing
 *
 * @example
 * <SideNav>
 *   <SideNav.Group label="Getting Started">
 *     <SideNav.Link id="intro" href="/intro" active>
 *       Introduction
 *     </SideNav.Link>
 *     <SideNav.Link id="installation" href="/installation">
 *       Installation
 *     </SideNav.Link>
 *   </SideNav.Group>
 *   <SideNav.Collapsible label="Components">
 *     <SideNav.Link id="button" href="/components/button">
 *       Button
 *     </SideNav.Link>
 *     <SideNav.Link id="input" href="/components/input">
 *       Input
 *     </SideNav.Link>
 *   </SideNav.Collapsible>
 * </SideNav>
 */
export const SideNav = Object.assign(
  forwardRef<HTMLElement, SideNavProps>(function SideNavInner(props, ref) {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const [activeIds, setActiveIds] = useState<Set<string>>(new Set());
    const { className, children, style, ...rest } = componentProps;

    const addActiveId = useCallback(
      (id: string) => {
        if (activeIds.has(id)) return;
        const newIds = new Set(activeIds);
        newIds.add(id);
        setActiveIds(newIds);
      },
      [activeIds],
    );

    const removeActiveId = useCallback(
      (id: string) => {
        if (!activeIds.has(id)) return;
        const newIds = new Set(activeIds);
        newIds.delete(id);
        setActiveIds(newIds);
      },
      [activeIds],
    );

    const sideNavRef = useRef<HTMLElement>(null);
    const [links, setLinks] = useState<SideNavContextProps["links"]>();

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    const reCalculateLinks = useCallback(() => {
      if (sideNavRef.current) {
        const items = sideNavRef.current.querySelectorAll(
          "li[data-anv='sidenav-link'] a:not([aria-disabled='true']), li[data-anv='sidenav-collapsible'] button[aria-expanded]:not([aria-disabled='true'])",
        );
        setLinks(items);
      }
    }, [sideNavRef]);

    useEffect(() => {
      reCalculateLinks();
    }, [reCalculateLinks]);

    const sidenavClassNames = cx([styles["sidenav"]], className);

    const value = useMemo(
      () => ({
        links,
        reCalculateLinks,
        activeIds,
        addActiveId,
        removeActiveId,
      }),
      [links, activeIds, addActiveId, removeActiveId, reCalculateLinks],
    );

    return (
      <SideNavProvider>
        <SideNavContext.Provider value={value}>
          <nav
            ref={useMergeRefs([sideNavRef, ref])}
            className={sidenavClassNames}
            style={styleCombined}
            data-anv="sidenav"
            {...rest}
          >
            <ul className={styles["root"]}>{children}</ul>
          </nav>
        </SideNavContext.Provider>
      </SideNavProvider>
    );
  }),
  {
    /**
     * SideNavLink component for individual navigation links.
     *
     * Features:
     * - Individual navigation links with active state support
     * - Keyboard navigation integration with arrow keys
     * - Focus management and tabindex handling
     * - Accessible with proper ARIA attributes
     * - Click and keyboard event handling
     * - Disabled state support
     *
     * @example
     * <SideNav.Link id="intro" href="/intro" active>
     *   Introduction
     * </SideNav.Link>
     */
    Link: SideNavLink,
    /**
     * SideNavCollapsible component for expandable navigation sections.
     *
     * Features:
     * - Expandable/collapsible navigation sections
     * - Controlled and uncontrolled expansion states
     * - Keyboard navigation with arrow keys for expansion
     * - Automatic expansion when child links are active
     * - Accessible with proper ARIA expanded states
     * - Visual indicators for active children
     *
     * @example
     * <SideNav.Collapsible label="Components">
     *   <SideNav.Link id="button" href="/components/button">
     *     Button
     *   </SideNav.Link>
     *   <SideNav.Link id="input" href="/components/input">
     *     Input
     *   </SideNav.Link>
     * </SideNav.Collapsible>
     */
    Collapsible: SideNavCollapsible,
    /**
     * SideNavGroup component for grouping related navigation links.
     *
     * Features:
     * - Groups related navigation links with labels
     * - Accessible with proper ARIA labeling
     * - Semantic grouping for screen readers
     * - Consistent styling and spacing
     *
     * @example
     * <SideNav.Group label="Getting Started">
     *   <SideNav.Link id="intro" href="/intro">
     *     Introduction
     *   </SideNav.Link>
     *   <SideNav.Link id="installation" href="/installation">
     *     Installation
     *   </SideNav.Link>
     * </SideNav.Group>
     */
    Group: SideNavGroup,
  },
);
SideNav.displayName = "SideNav";
